import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  TextField,
} from "@material-ui/core";
import RestaurantIcon from "@material-ui/icons/Restaurant";
import ReplayIcon from "@material-ui/icons/Replay";
import SaveIcon from "@material-ui/icons/Save";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const initialProfile = {
  companyName: "",
  businessType: "delivery",
  openingHours: "terca a domingo das 18:30 as 23:00",
  menuUrl: "",
};

const businessProfiles = {
  delivery: {
    label: "Delivery geral",
    emoji: "🍽️",
    keyword: "Comida",
    specialty: "as opcoes de hoje",
    craving: "Bateu fome?",
    menuLabel: "cardapio completo",
  },
  restaurante: {
    label: "Restaurante",
    emoji: "🍽️",
    keyword: "Prato",
    specialty: "nossos pratos",
    craving: "Quer escolher seu prato de hoje?",
    menuLabel: "cardapio completo",
  },
  hamburgueria: {
    label: "Hamburgueria",
    emoji: "🍔",
    keyword: "Hamburguer",
    specialty: "nossos hamburgueres",
    craving: "Bateu vontade de hamburguer?",
    menuLabel: "cardapio de hamburgueres",
  },
  pizzaria: {
    label: "Pizzaria",
    emoji: "🍕",
    keyword: "Pizza",
    specialty: "nossas pizzas",
    craving: "Bateu vontade de pizza?",
    menuLabel: "cardapio de pizzas",
  },
  acaiteria: {
    label: "Acaiteria",
    emoji: "🍧",
    keyword: "Acai",
    specialty: "nossos acais",
    craving: "Bateu vontade de acai?",
    menuLabel: "cardapio de acais",
  },
  japonesa: {
    label: "Comida japonesa",
    emoji: "🍣",
    keyword: "Sushi",
    specialty: "nossas opcoes japonesas",
    craving: "Bateu vontade de comida japonesa?",
    menuLabel: "cardapio japones",
  },
};

const conditionLabels = {
  contains: "Contem",
  startsWith: "Comeca com",
  endsWith: "Termina com",
  equal: "Igual",
};

const getBusinessProfile = (businessType) =>
  businessProfiles[businessType] || businessProfiles.delivery;

const getCompanyName = (companyName) =>
  companyName.trim() || "Nome da empresa";

const getMenuText = (menuUrl, fallback) => {
  const cleanUrl = menuUrl.trim();

  if (cleanUrl) {
    return `👇\n${cleanUrl}`;
  }

  return fallback;
};

const buildDeliveryReplies = (profile) => {
  const business = getBusinessProfile(profile.businessType);
  const company = getCompanyName(profile.companyName);
  const companyMention = `*${company}*`;
  const hours = profile.openingHours.trim() || "informe o horario de funcionamento";
  const menuText = getMenuText(
    profile.menuUrl,
    "Se preferir, envie uma mensagem por aqui que ajudamos voce a escolher."
  );
  const shortMenuText = getMenuText(
    profile.menuUrl,
    "Me envie uma mensagem por aqui que ajudamos voce a fazer o pedido."
  );

  return [
    {
      keyword: "Boa noite",
      condition: "contains",
      response: `Boa noite! Bem-vindo(a) ao ${companyMention} ${business.emoji}\n\nHorario de funcionamento: ${hours}.\n\nPara ver o ${business.menuLabel} e fazer seu pedido, clique aqui ${menuText}`,
    },
    {
      keyword: "Boa tarde",
      condition: "contains",
      response: `Boa tarde! ${business.emoji} Aqui e o ${companyMention}. Que tal escolher ${business.specialty}? Veja nosso ${business.menuLabel}: ${shortMenuText}`,
    },
    {
      keyword: "Bom dia",
      condition: "contains",
      response: `Bom dia! ${business.emoji} Aqui e o ${companyMention}. Veja nossas opcoes e faca seu pedido: ${shortMenuText}`,
    },
    {
      keyword: "Cardapio",
      condition: "contains",
      response: `Aqui esta o nosso ${business.menuLabel} ${business.emoji}\n${shortMenuText}`,
    },
    {
      keyword: "Cardápio",
      condition: "contains",
      response: `Aqui esta o nosso ${business.menuLabel} ${business.emoji}\n${shortMenuText}`,
    },
    {
      keyword: "Menu",
      condition: "contains",
      response: `Aqui esta o nosso ${business.menuLabel} ${business.emoji}\n${shortMenuText}`,
    },
    {
      keyword: "Delivery",
      condition: "endsWith",
      response: `Faca seu pedido pelo nosso delivery ${business.emoji}🛵\n${shortMenuText}`,
    },
    {
      keyword: "Pedido",
      condition: "endsWith",
      response: `Para fazer seu pedido e so clicar no link abaixo e escolher seus favoritos ${business.emoji}\n${shortMenuText}`,
    },
    {
      keyword: "Horario",
      condition: "endsWith",
      response: `Horario de funcionamento: ${hours}.`,
    },
    {
      keyword: "Oi",
      condition: "startsWith",
      response: `Oi! Seja bem-vindo(a) ao ${companyMention}. Como posso te ajudar hoje? ${business.emoji}`,
    },
    {
      keyword: "Ola",
      condition: "startsWith",
      response: `Ola! Seja bem-vindo(a) ao ${companyMention}. Quer ver nosso ${business.menuLabel}? ${shortMenuText}`,
    },
    {
      keyword: "Olá",
      condition: "startsWith",
      response: `Ola! Seja bem-vindo(a) ao ${companyMention}. Quer ver nosso ${business.menuLabel}? ${shortMenuText}`,
    },
    {
      keyword: business.keyword,
      condition: "endsWith",
      response: `${business.craving} ${business.emoji} Veja ${business.specialty} e faca seu pedido pelo delivery:\n${shortMenuText}`,
    },
  ];
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark";
  const primary = theme.palette.primary.main;

  return {
    dialogPaper: {
      borderRadius: 10,
      color: isDark ? "#f8fafc" : "#131b2e",
      backgroundColor: isDark ? "#424242" : "#ffffff",
      boxShadow: isDark
        ? "0 24px 56px rgba(0, 0, 0, 0.42)"
        : "0 24px 56px rgba(15, 23, 42, 0.16)",
    },
    dialogTitle: {
      padding: theme.spacing(2.5, 3, 1.5),
    },
    titleRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
    },
    titleIcon: {
      width: 42,
      height: 42,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ffffff",
      background: `linear-gradient(135deg, ${primary} 0%, #0052cc 100%)`,
    },
    dialogEyebrow: {
      marginBottom: theme.spacing(0.5),
      color: isDark ? "#93c5fd" : "#4c5d8d",
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: 0.5,
      textTransform: "uppercase",
    },
    dialogTitleText: {
      color: isDark ? "#ffffff" : "#131b2e",
      fontSize: 22,
      fontWeight: 800,
      lineHeight: 1.2,
    },
    dialogContent: {
      padding: theme.spacing(1, 3, 2),
      backgroundColor: isDark ? "#424242" : "#ffffff",
    },
    field: {
      marginBottom: theme.spacing(1.5),
      "& .MuiInputLabel-root": {
        color: isDark ? "#d1d5db" : "#4b5563",
        fontWeight: 600,
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: primary,
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: 8,
        color: isDark ? "#ffffff" : "#131b2e",
        backgroundColor: isDark ? "#4e4e4e" : "#f8fbff",
        "& fieldset": {
          borderColor: isDark
            ? "rgba(255, 255, 255, 0.24)"
            : "rgba(195, 198, 214, 0.65)",
        },
        "&:hover fieldset": {
          borderColor: isDark
            ? "rgba(147, 197, 253, 0.72)"
            : "rgba(0, 82, 204, 0.55)",
        },
        "&.Mui-focused fieldset": {
          borderColor: primary,
          borderWidth: 1,
        },
      },
      "& .MuiSelect-icon": {
        color: isDark ? "#d1d5db" : "inherit",
      },
    },
    helperText: {
      margin: theme.spacing(0, 0, 2),
      color: isDark ? "#cbd5e1" : "#5f6472",
      fontSize: 13,
      lineHeight: 1.5,
    },
    regenerateButton: {
      borderRadius: 8,
      color: isDark ? "#dbeafe" : "#0040a2",
      borderColor: isDark
        ? "rgba(147, 197, 253, 0.42)"
        : "rgba(0, 82, 204, 0.42)",
      textTransform: "none",
      fontWeight: 700,
      "&:hover": {
        borderColor: primary,
        backgroundColor: isDark ? "rgba(0, 82, 204, 0.18)" : "#eef4ff",
      },
    },
    previewHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      margin: theme.spacing(2, 0, 1),
      [theme.breakpoints.down("xs")]: {
        alignItems: "stretch",
        flexDirection: "column",
      },
    },
    previewTitle: {
      color: isDark ? "#f8fafc" : "#131b2e",
      fontSize: 16,
      fontWeight: 800,
    },
    previewCount: {
      color: isDark ? "#94a3b8" : "#737685",
      fontSize: 12,
      fontWeight: 700,
    },
    phraseList: {
      display: "grid",
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(1.5),
    },
    phraseCard: {
      padding: theme.spacing(1.5),
      borderRadius: 8,
      backgroundColor: isDark ? "#4e4e4e" : "#f8fbff",
      border: `1px solid ${
        isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(195, 198, 214, 0.55)"
      }`,
    },
    conditionField: {
      minWidth: 150,
    },
    dialogActions: {
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 3, 2.5),
      backgroundColor: isDark ? "#424242" : "#ffffff",
      [theme.breakpoints.down("xs")]: {
        alignItems: "stretch",
        flexDirection: "column",
        "& > *": {
          marginLeft: "0 !important",
        },
      },
    },
    cancelButton: {
      borderRadius: 8,
      color: isDark ? "#e5e7eb" : "#4b5563",
      textTransform: "none",
      fontWeight: 700,
      "&:hover": {
        backgroundColor: isDark ? "#4e4e4e" : "#f3f4f6",
      },
    },
    saveButton: {
      borderRadius: 8,
      minWidth: 172,
      color: "#ffffff",
      background: `linear-gradient(135deg, ${primary} 0%, #0052cc 100%)`,
      boxShadow: isDark
        ? "0 10px 22px rgba(0, 0, 0, 0.28)"
        : "0 10px 22px rgba(0, 82, 204, 0.22)",
      textTransform: "none",
      fontWeight: 800,
      "&:hover": {
        background: "linear-gradient(135deg, #0040a2 0%, #0052cc 100%)",
      },
      "&.Mui-disabled": {
        color: "rgba(255, 255, 255, 0.72)",
        background: isDark ? "#52606d" : "#9aa8c3",
      },
    },
    saveProgress: {
      marginRight: theme.spacing(1),
      color: "inherit",
    },
  };
});

const AutoReplyDeliveryDialog = ({ open, onClose, onCreated }) => {
  const classes = useStyles();
  const [profile, setProfile] = useState(initialProfile);
  const [replies, setReplies] = useState(() => buildDeliveryReplies(initialProfile));
  const [manualReplies, setManualReplies] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!manualReplies) {
      setReplies(buildDeliveryReplies(profile));
    }
  }, [profile, manualReplies]);

  const handleProfileChange = (field) => (event) => {
    const { value } = event.target;
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReplyChange = (index, field) => (event) => {
    const { value } = event.target;
    setManualReplies(true);
    setReplies((prev) =>
      prev.map((reply, replyIndex) =>
        replyIndex === index
          ? {
              ...reply,
              [field]: value,
            }
          : reply
      )
    );
  };

  const handleRegenerateReplies = () => {
    setReplies(buildDeliveryReplies(profile));
    setManualReplies(false);
  };

  const resetDialog = () => {
    setProfile(initialProfile);
    setReplies(buildDeliveryReplies(initialProfile));
    setManualReplies(false);
    setSaving(false);
  };

  const handleClose = () => {
    resetDialog();
    onClose();
  };

  const handleSave = async () => {
    const companyName = profile.companyName.trim();
    const openingHours = profile.openingHours.trim();
    const validReplies = replies.filter(
      (reply) =>
        reply.keyword.trim() &&
        reply.condition.trim() &&
        reply.response.trim()
    );

    if (!companyName) {
      toast.error("Informe o nome da empresa para gerar frases prontas.");
      return;
    }

    if (!openingHours) {
      toast.error("Informe o horario de funcionamento.");
      return;
    }

    if (!validReplies.length) {
      toast.error("Mantenha pelo menos uma frase preenchida antes de salvar.");
      return;
    }

    try {
      setSaving(true);
      await Promise.all(
        validReplies.map((reply) =>
          api.post("/auto-replies", {
            keyword: reply.keyword.trim(),
            condition: reply.condition,
            response: reply.response.trim(),
          })
        )
      );
      toast.success(`${validReplies.length} frases de delivery criadas com sucesso.`);

      if (typeof onCreated === "function") {
        await onCreated();
      }

      handleClose();
    } catch (err) {
      setSaving(false);
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleRow}>
          <Box className={classes.titleIcon}>
            <RestaurantIcon />
          </Box>
          <Box>
            <Box className={classes.dialogEyebrow}>Modelo pronto</Box>
            <Box className={classes.dialogTitleText}>Usar frases Delivery</Box>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <p className={classes.helperText}>
          Preencha os dados uma vez, revise as frases abaixo e salve tudo como
          respostas automaticas. Depois elas continuam editaveis uma por uma na
          tabela.
        </p>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              className={classes.field}
              label="Nome da empresa"
              value={profile.companyName}
              onChange={handleProfileChange("companyName")}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              placeholder="Ex: Pepperoni, Burger House, Restaurante Central"
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              className={classes.field}
              select
              label="Tipo de delivery"
              value={profile.businessType}
              onChange={handleProfileChange("businessType")}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              disabled={saving}
            >
              {Object.keys(businessProfiles).map((key) => (
                <MenuItem key={key} value={key}>
                  {businessProfiles[key].label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              className={classes.field}
              label="Horario de funcionamento"
              value={profile.openingHours}
              onChange={handleProfileChange("openingHours")}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              placeholder="Ex: segunda a sabado das 18:00 as 23:30"
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              className={classes.field}
              label="Link do cardapio ou pedido"
              value={profile.menuUrl}
              onChange={handleProfileChange("menuUrl")}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              placeholder="https://..."
              disabled={saving}
            />
          </Grid>
        </Grid>

        <Box className={classes.previewHeader}>
          <Box>
            <Box className={classes.previewTitle}>Frases que serao criadas</Box>
            <Box className={classes.previewCount}>
              {replies.length} respostas automaticas prontas
            </Box>
          </Box>
          <Button
            className={classes.regenerateButton}
            variant="outlined"
            startIcon={<ReplayIcon />}
            onClick={handleRegenerateReplies}
            disabled={saving}
          >
            Atualizar frases
          </Button>
        </Box>

        <Divider />

        <Box className={classes.phraseList}>
          {replies.map((reply, index) => (
            <Box className={classes.phraseCard} key={`${reply.keyword}-${index}`}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    className={classes.field}
                    label="Palavra"
                    value={reply.keyword}
                    onChange={handleReplyChange(index, "keyword")}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    size="small"
                    disabled={saving}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    className={`${classes.field} ${classes.conditionField}`}
                    select
                    label="Condicao"
                    value={reply.condition}
                    onChange={handleReplyChange(index, "condition")}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    size="small"
                    disabled={saving}
                  >
                    {Object.keys(conditionLabels).map((key) => (
                      <MenuItem key={key} value={key}>
                        {conditionLabels[key]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    className={classes.field}
                    label="Resposta"
                    value={reply.response}
                    onChange={handleReplyChange(index, "response")}
                    fullWidth
                    multiline
                    rows={3}
                    margin="dense"
                    variant="outlined"
                    disabled={saving}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} className={classes.cancelButton} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          className={classes.saveButton}
          startIcon={saving ? null : <SaveIcon />}
          disabled={saving}
        >
          {saving && <CircularProgress size={16} className={classes.saveProgress} />}
          {saving ? "Criando frases..." : "Criar frases"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoReplyDeliveryDialog;
